import { renderToStaticMarkup } from "react-dom/server";
import { ChatAppResponse, getCitationFilePath } from "../../api";
import { QueryPlanStep, getStepLabel, activityTypeLabels } from "../AnalysisPanel/agentPlanUtils";

export type CitationDetail = {
    reference: string;
    /**
     * Hierarchical citation index, formatted as "<fileIndex>.<pageIndex>".
     * - fileIndex: 1-based ordinal of the unique source (filename or web URL) in
     *   the order it first appears in the answer.
     * - pageIndex: 1-based ordinal of the unique page/anchor within that source
     *   in the order pages first appear. Web citations always have pageIndex=1.
     * Inline superscripts and the bottom citation list both render this string,
     * so "1.2" inline and "1.2" in the citation list are unambiguously the same.
     */
    index: string;
    isWeb: boolean;
    activityId?: string;
    stepNumber?: number;
    stepLabel?: string;
    stepSource?: string;
};

type CitationFragment =
    | { type: "text"; value: string }
    | {
          type: "citation";
          detail: CitationDetail;
      };

type ActivityStepMeta = {
    stepNumber: number;
    stepLabel: string;
};

type HtmlParsedAnswer = {
    answerHtml: string;
    citations: CitationDetail[];
};

const isWebCitation = (reference: string) => reference.startsWith("http://") || reference.startsWith("https://");

const normalizeAnswerText = (answer: ChatAppResponse, isStreaming: boolean): string => {
    // Normalize lenticular brackets 【...】 to square brackets [...] so the citation
    // parser handles both styles regardless of which format the model emits.
    let parsedAnswer = answer.message.content.trim().replace(/【([^】]+)】/g, "[$1]");

    if (isStreaming) {
        let lastIndex = parsedAnswer.length;
        for (let i = parsedAnswer.length - 1; i >= 0; i--) {
            if (parsedAnswer[i] === "]") {
                break;
            } else if (parsedAnswer[i] === "[") {
                lastIndex = i;
                break;
            }
        }
        parsedAnswer = parsedAnswer.substring(0, lastIndex);
    }

    return parsedAnswer;
};

const buildActivityStepMap = (answer: ChatAppResponse): Record<string, ActivityStepMeta> => {
    const mapping: Record<string, ActivityStepMeta> = {};
    const thoughts = answer.context?.thoughts;
    if (!Array.isArray(thoughts)) {
        return mapping;
    }

    const thoughtWithPlan = thoughts.find(thought => Array.isArray(thought.props?.query_plan));
    if (!thoughtWithPlan) {
        return mapping;
    }

    const planSteps = (thoughtWithPlan.props?.query_plan as QueryPlanStep[]) ?? [];
    planSteps.forEach((step, index) => {
        if (step && step.id !== undefined && step.id !== null) {
            mapping[String(step.id)] = {
                stepNumber: index + 1,
                stepLabel: getStepLabel(step)
            };
        }
    });

    return mapping;
};

const collectCitations = (answer: ChatAppResponse, isStreaming: boolean): { fragments: CitationFragment[]; citations: CitationDetail[] } => {
    const possibleCitations = answer.context?.data_points?.citations || [];
    const citationActivityDetails = answer.context?.data_points?.citation_activity_details ?? {};
    const activitySteps = buildActivityStepMap(answer);
    const externalResults = answer.context?.data_points?.external_results_metadata || [];
    const parsedAnswer = normalizeAnswerText(answer, isStreaming);
    const parts = parsedAnswer.split(/\[([^\]]+)\]/g);

    // Helper to resolve SharePoint filename to URL
    const resolveSharePointUrl = (citation: string): string => {
        // If it's already a URL, return as-is
        if (isWebCitation(citation)) {
            return citation;
        }
        // Check if this looks like a filename (has an extension)
        const hasFileExtension = /\.(pdf|docx?|xlsx?|pptx?|txt|html?|csv)$/i.test(citation);
        if (!hasFileExtension) {
            return citation;
        }

        // Look for matching SharePoint URL in external_results_metadata
        // Match by checking if the URL ends with the filename
        const matchingResult = externalResults.find(result => {
            if (!result.url) return false;
            const urlParts = result.url.split("/");
            const urlFilename = urlParts[urlParts.length - 1];
            return urlFilename === citation || decodeURIComponent(urlFilename) === citation;
        });

        return matchingResult?.url || citation;
    };

    const fragments: CitationFragment[] = [];
    const citationMap = new Map<string, CitationDetail>();
    const citationList: CitationDetail[] = [];
    // Hierarchical index bookkeeping. Each unique source (filename or web URL) gets
    // a 1-based fileIndex; each unique page/anchor within that source gets a 1-based
    // pageIndex. The displayed index is "<fileIndex>.<pageIndex>".
    const fileIndices = new Map<string, number>();
    const pagesPerFile = new Map<string, Map<string, number>>();

    parts.forEach((part, index) => {
        if (index % 2 === 0) {
            fragments.push({ type: "text", value: part });
            return;
        }

        // A bracket counts as a citation if (a) we know about it from data_points,
        // (b) it's a bare URL — self-identifying and always safe to render as a
        // clickable web reference even if it didn't make it into data_points.citations
        // due to extraction edge cases (trailing punctuation, encoding drift, etc.),
        // or (c) the bare filename (everything before "#") matches a known citation —
        // covers the case where the model appends a hallucinated "#page=N" to a
        // non-paginated source (e.g. .md / .txt / .csv) whose indexed sourcepage has
        // no page anchor. The "#page=N" suffix is a URL fragment so it doesn't break
        // file retrieval — we just need to recognize the bracket as valid.
        const stripAnchor = (s: string) => s.split("#")[0];
        const partFile = stripAnchor(part);
        const isValidCitation =
            possibleCitations.some(citation => citation.endsWith(part)) ||
            isWebCitation(part) ||
            (!!partFile && possibleCitations.some(citation => stripAnchor(citation) === partFile));
        if (!isValidCitation) {
            fragments.push({ type: "text", value: `[${part}]` });
            return;
        }

        // Resolve SharePoint filename to URL if applicable
        const resolvedReference = resolveSharePointUrl(part);

        // Check if this resolved reference already exists
        const existing = citationMap.get(resolvedReference);
        if (existing) {
            fragments.push({ type: "citation", detail: existing });
            return;
        }

        // Compute hierarchical index. Treat each web URL as its own single-page source.
        const isWebRef = isWebCitation(resolvedReference);
        let fileKey: string;
        let pageKey: string;
        if (isWebRef) {
            fileKey = resolvedReference;
            pageKey = "1";
        } else {
            const [file, hashPart] = resolvedReference.split("#");
            fileKey = file;
            pageKey = hashPart || "_";
        }
        if (!fileIndices.has(fileKey)) {
            fileIndices.set(fileKey, fileIndices.size + 1);
            pagesPerFile.set(fileKey, new Map());
        }
        const fileIdx = fileIndices.get(fileKey)!;
        const pageMap = pagesPerFile.get(fileKey)!;
        if (!pageMap.has(pageKey)) {
            pageMap.set(pageKey, pageMap.size + 1);
        }
        const pageIdx = pageMap.get(pageKey)!;
        const hierarchicalIndex = `${fileIdx}.${pageIdx}`;

        const backendDetail = citationActivityDetails?.[part];
        const activityId = backendDetail?.id;
        const stepMeta = activityId ? activitySteps[String(activityId)] : undefined;

        // Get label from backend type using our mapping, or fallback to stepMeta
        const activityLabel = backendDetail?.type ? activityTypeLabels[backendDetail.type] || backendDetail.type : undefined;

        const detail: CitationDetail = {
            reference: resolvedReference,
            index: hierarchicalIndex,
            isWeb: isWebRef,
            activityId: activityId !== undefined ? String(activityId) : undefined,
            stepNumber: backendDetail?.number ?? stepMeta?.stepNumber,
            stepLabel: activityLabel ?? stepMeta?.stepLabel,
            stepSource: backendDetail?.source
        };

        citationMap.set(resolvedReference, detail);
        citationList.push(detail);
        fragments.push({ type: "citation", detail });
    });

    return { fragments, citations: citationList };
};

// Inline citation HTML carries the resolved file path on a data-* attribute. The actual
// click handler lives on the markdown wrapper in Answer.tsx (event delegation), because
// renderToStaticMarkup discards onClick and DOMPurify would strip onclick="" anyway.
// data-* attributes survive both, and ReactMarkdown re-parses them as plain HTML.
export const CITATION_PATH_ATTR = "data-citation-path";

const renderCitation = (detail: CitationDetail, conversationId: string | null) => {
    const stepBadgeLabel = detail.stepSource ?? detail.stepLabel;
    const stepBadgeTitle =
        detail.stepNumber !== undefined
            ? `Linked to Step ${detail.stepNumber}${detail.stepLabel ? `: ${detail.stepLabel}` : ""}${detail.stepSource ? ` (${detail.stepSource})` : ""}`
            : stepBadgeLabel
              ? `Linked to ${stepBadgeLabel}`
              : undefined;
    const supElement = <sup title={stepBadgeTitle ?? undefined}>{detail.index}</sup>;

    // Inline citations render as clickable from the moment they appear in the stream.
    // The full citation list is delivered in the initial context event before any text
    // deltas, so links/indices are resolvable from the first chunk; the parser also
    // truncates partial trailing brackets in normalizeAnswerText, so a half-streamed
    // "[THOMP" never reaches this renderer until the closing "]" arrives.
    if (detail.isWeb) {
        return renderToStaticMarkup(
            <span className="citationBadgeContainer">
                <a className="supContainer" title={detail.reference} href={detail.reference} target="_blank" rel="noopener noreferrer">
                    {supElement}
                </a>
            </span>
        );
    }

    const path = getCitationFilePath(detail.reference, conversationId);
    return renderToStaticMarkup(
        <span className="citationBadgeContainer">
            <a className="supContainer" title={detail.reference} {...{ [CITATION_PATH_ATTR]: path }}>
                {supElement}
            </a>
        </span>
    );
};

export function parseAnswerToHtml(answer: ChatAppResponse, isStreaming: boolean): HtmlParsedAnswer {
    const { fragments, citations } = collectCitations(answer, isStreaming);
    const conversationId = typeof answer.session_state === "string" ? answer.session_state : null;
    const answerHtml = fragments
        .map(fragment => (fragment.type === "text" ? fragment.value : renderCitation(fragment.detail, conversationId)))
        .join("");

    return {
        answerHtml,
        citations
    };
}

export function extractCitationDetails(answer: ChatAppResponse, isStreaming = false): CitationDetail[] {
    return collectCitations(answer, isStreaming).citations;
}
